import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';

interface AdminDashboardProps {
  admin: any;
  sessionToken: string;
  onLogout: () => void;
}

interface DashboardData {
  userStats: any;
  tokenStats: any;
  transactionStats: any;
  topTokens: any[];
  recentUsers: any[];
  recentTransactions: any[];
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ admin, sessionToken, onLogout }) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch('/api/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setDashboardData(data.dashboard);
      } else {
        setError(data.error || 'Failed to load dashboard');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/admin/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('adminSessionToken');
      localStorage.removeItem('adminProfile');
      onLogout();
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="admin-dashboard-loading">
        <div className="loading-spinner">⏳</div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard-error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button onClick={fetchDashboardData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-left">
          <h1>🛡️ Admin Dashboard</h1>
          <p>Welcome back, <strong>{admin.username}</strong></p>
        </div>
        <div className="header-right">
          <div className="admin-info">
            <span className="admin-role">{admin.role}</span>
            <button className="logout-btn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users
        </button>
        <button 
          className={`tab ${activeTab === 'tokens' ? 'active' : ''}`}
          onClick={() => setActiveTab('tokens')}
        >
          🪙 Tokens
        </button>
        <button 
          className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          💸 Transactions
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && dashboardData && (
          <div className="overview-tab">
            <div className="stats-grid">
              <div className="stat-card users">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <h3>Total Users</h3>
                  <div className="stat-number">{formatNumber(dashboardData.userStats.total_users)}</div>
                  <div className="stat-detail">
                    +{dashboardData.userStats.new_today} today
                  </div>
                </div>
              </div>

              <div className="stat-card tokens">
                <div className="stat-icon">🪙</div>
                <div className="stat-content">
                  <h3>Total Tokens</h3>
                  <div className="stat-number">{formatNumber(dashboardData.tokenStats.total_tokens)}</div>
                  <div className="stat-detail">
                    +{dashboardData.tokenStats.created_today} today
                  </div>
                </div>
              </div>

              <div className="stat-card transactions">
                <div className="stat-icon">💸</div>
                <div className="stat-content">
                  <h3>Total Transactions</h3>
                  <div className="stat-number">{formatNumber(dashboardData.transactionStats.total_transactions)}</div>
                  <div className="stat-detail">
                    +{dashboardData.transactionStats.transactions_today} today
                  </div>
                </div>
              </div>

              <div className="stat-card volume">
                <div className="stat-icon">📈</div>
                <div className="stat-content">
                  <h3>Total Volume</h3>
                  <div className="stat-number">{formatCurrency(dashboardData.transactionStats.total_volume || 0)}</div>
                  <div className="stat-detail">
                    {formatCurrency(dashboardData.transactionStats.volume_today || 0)} today
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-sections">
              <div className="section">
                <h3>🏆 Top Tokens (7 days)</h3>
                <div className="top-tokens-list">
                  {dashboardData.topTokens.slice(0, 5).map((token, index) => (
                    <div key={index} className="top-token-item">
                      <div className="token-rank">#{index + 1}</div>
                      <div className="token-info">
                        <div className="token-name">{token.name} ({token.symbol})</div>
                        <div className="token-creator">by {token.creator_username || 'Anonymous'}</div>
                      </div>
                      <div className="token-stats">
                        <div className="token-volume">{formatCurrency(token.total_volume || 0)}</div>
                        <div className="token-trades">{token.transaction_count} trades</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="section">
                <h3>🕒 Recent Activity</h3>
                <div className="recent-activity">
                  {dashboardData.recentTransactions.slice(0, 8).map((tx, index) => (
                    <div key={index} className="activity-item">
                      <div className={`activity-type ${tx.transaction_type}`}>
                        {tx.transaction_type === 'buy' ? '🟢' : '🔴'}
                      </div>
                      <div className="activity-info">
                        <div className="activity-user">{tx.username || 'Anonymous'}</div>
                        <div className="activity-action">
                          {tx.transaction_type} {tx.token_symbol}
                        </div>
                      </div>
                      <div className="activity-amount">
                        {formatCurrency(tx.sui_amount)}
                      </div>
                      <div className="activity-time">
                        {formatDate(tx.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-tab">
            <div className="section-header">
              <h3>👥 User Management</h3>
              <div className="section-actions">
                <button className="refresh-btn" onClick={fetchDashboardData}>
                  🔄 Refresh
                </button>
              </div>
            </div>
            <div className="users-list">
              {dashboardData?.recentUsers.map((user, index) => (
                <div key={index} className="user-item">
                  <div className="user-avatar">
                    {user.username ? user.username[0].toUpperCase() : '👤'}
                  </div>
                  <div className="user-info">
                    <div className="user-name">{user.username || 'Anonymous'}</div>
                    <div className="user-wallet">
                      {user.wallet_address.slice(0, 8)}...{user.wallet_address.slice(-6)}
                    </div>
                  </div>
                  <div className="user-stats">
                    <div className="user-trades">{user.total_trades} trades</div>
                    <div className="user-volume">{formatCurrency(user.total_volume || 0)}</div>
                  </div>
                  <div className="user-status">
                    <div className="status-indicator active"></div>
                    {user.last_login ? formatDate(user.last_login) : 'Never'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add more tabs content as needed */}
      </div>
    </div>
  );
};

export default AdminDashboard;
